import type { IntegrationService } from '@axilon-apps/domains-ontology-ui-state'

/**
 * Lowercase, hyphenated slug safe to embed in a secret's logical `key`.
 * Collapses any run of non-alphanumeric characters to a single hyphen
 * and trims leading/trailing hyphens.
 */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Builds the logical name (the secret's `key`, which becomes the
 * integration's `api_key_name`) for an integration's API key.
 *
 * Secret keys must be unique per org, so we append a short random suffix
 * to avoid 409 collisions when two integrations share a service +
 * instance name. The name is purely internal plumbing — it is never the
 * key value — so a human-readable prefix plus entropy is ideal: it stays
 * legible in the secrets list while remaining collision-resistant.
 *
 * Rotation reuses the existing key (only the value changes), so this is
 * only called when first attaching a credential.
 */
export function buildSecretKey(
  service: IntegrationService,
  instanceName: string,
  randomSuffix: string = generateSuffix()
): string {
  const instanceSlug = slugify(instanceName)
  const base = instanceSlug ? `${service}-${instanceSlug}` : service
  return `${base}-api-key-${randomSuffix}`
}

/** 8-char lowercase base36 suffix. Crypto-backed when available. */
function generateSuffix(): string {
  const cryptoObj = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const buf = new Uint32Array(2)
    cryptoObj.getRandomValues(buf)
    return Array.from(buf, (n) => n.toString(36))
      .join('')
      .slice(0, 8)
  }
  return Math.random().toString(36).slice(2, 10).padEnd(8, '0')
}
